// src/auth/KeycloakProvider.tsx
import React, { createContext, useContext, useEffect, useState } from "react";
import Keycloak, { KeycloakInitOptions } from "keycloak-js";
import keycloak from "./keycloak";

type AuthContextType = {
  keycloak: Keycloak;
  initialized: boolean;
  authenticated: boolean;
  token?: string;
};

const AuthContext = createContext<AuthContextType | null>(null);
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside KeycloakProvider");
  return ctx;
};

export const KeycloakProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [token, setToken] = useState<string | undefined>();

  useEffect(() => {
    const initOptions: KeycloakInitOptions = {
      onLoad: "check-sso",
      silentCheckSsoRedirectUri:
        window.location.origin + "/silent-check-sso.html",
      pkceMethod: "S256",
    };

    keycloak.init(initOptions).then((auth) => {
      setAuthenticated(auth);
      setInitialized(true);
      setToken(keycloak.token);

      if (!auth) {
        keycloak.login();
        return;
      }

      keycloak.onTokenExpired = () => {
        keycloak
          .updateToken(60)
          .then(() => setToken(keycloak.token))
          .catch(() => {
            console.warn("Token refresh failed");
            keycloak.logout();
          });
      };
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        keycloak,
        initialized,
        authenticated,
        token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
